/* eslint-disable react-refresh/only-export-components */
import { memo } from 'react';
import {
  ArrowRight,
  Clock,
  Key,
  ShieldCheck,
  ShieldOff,
  UserMinus,
  Users,
} from 'lucide-react';
import { Badge } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';
import type { RbacAuditEntry } from './types';

/**
 * The audit log's per-action renderers — one sentence per row.
 *
 * Every app's RBAC backend emits the same action codes (they were cloned from the
 * projects-api surface), so the copy lives here once rather than being rewritten
 * per app. An action with no renderer falls back to key:value chips from
 * `detail_json`, so a NEW action code shows something useful the day it ships
 * instead of an empty cell.
 */

/** Action code → the sentence the row reads as. */
export const RBAC_AUDIT_ACTION_LABELS: Record<string, string> = {
  CREATE_TEMPLATE: 'Template created',
  UPDATE_TEMPLATE: 'Template updated',
  DELETE_TEMPLATE: 'Template deleted',
  ASSIGN_TEMPLATE: 'Template assigned',
  BULK_ASSIGN_TEMPLATE: 'Bulk assigned',
  UPDATE_OVERRIDES: 'Override updated',
  REMOVE_USER_PERMISSION: 'Reset to default',
  GRANT_ELEVATION: 'Elevation granted',
  REVOKE_ELEVATION: 'Elevation revoked',
};

/** `GRANT_ELEVATION` → `Grant elevation` — the fallback for an unknown code. */
export function humanizeAuditAction(action: string): string {
  const words = action.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Plain text, no chip. The action is the row's SUBJECT — every row has one, and a
 * column where 100% of cells are a coloured pill spends the loudest treatment on
 * the least surprising fact, while the Details beside it (which genuinely vary)
 * get read second.
 */
export function AuditActionText({ action }: { action: string }) {
  return (
    <span className="text-foreground text-lg font-medium">
      {RBAC_AUDIT_ACTION_LABELS[action] ?? humanizeAuditAction(action)}
    </span>
  );
}

/** Tone ramp for the detail badges — categorical, not semantic state. */
const TONE = {
  teal: { icon: 'text-teal-600', badge: 'border-teal-200 bg-teal-50 text-teal-700' },
  indigo: { icon: 'text-indigo-600', badge: 'border-indigo-200 bg-indigo-50 text-indigo-700' },
  green: { icon: 'text-emerald-600', badge: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  red: { icon: 'text-red-600', badge: 'border-red-200 bg-red-50 text-red-700' },
  amber: { icon: 'text-amber-600', badge: 'border-amber-200 bg-amber-50 text-amber-700' },
  violet: { icon: 'text-violet-600', badge: 'border-violet-200 bg-violet-50 text-violet-700' },
  cyan: { icon: 'text-cyan-600', badge: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
} as const;

type Tone = keyof typeof TONE;

function countKeys(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  return Object.keys(value as Record<string, unknown>).length;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function shortValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `${v.length} item${v.length !== 1 ? 's' : ''}`;
  const n = countKeys(v);
  return `${n} key${n !== 1 ? 's' : ''}`;
}

function Line({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <span className="inline-flex flex-wrap items-center gap-1.5 text-lg">{children}</span>
    </div>
  );
}

function Chip({ tone, children }: { tone?: Tone; children: React.ReactNode }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'h-5 text-xs font-normal',
        tone ? TONE[tone].badge : 'bg-muted/40 border-border',
      )}
    >
      {children}
    </Badge>
  );
}

export interface AuditDetailCellProps {
  log: RbacAuditEntry;
  resolveUser: (id: string | null) => string;
  resolveTemplate: (id: unknown) => string;
  formatDateTime: (iso: string) => string;
}

export const AuditDetailCell = memo(function AuditDetailCell({
  log,
  resolveUser,
  resolveTemplate,
  formatDateTime,
}: AuditDetailCellProps) {
  const targetUser = resolveUser(log.targetUserId);
  const hasTarget = log.targetUserId != null && targetUser !== '—';
  const detail = log.detail ?? {};
  const Target = () =>
    hasTarget ? <span className="text-foreground font-medium">{targetUser}</span> : null;

  switch (log.action) {
    case 'CREATE_TEMPLATE':
      return (
        <Line>
          <ShieldCheck className={cn('h-3 w-3 shrink-0', TONE.teal.icon)} strokeWidth={1.5} />
          <span className="text-muted-foreground">created template</span>
          <Chip tone="teal">{asString(detail.name) ?? resolveTemplate(detail.template_id)}</Chip>
        </Line>
      );

    case 'UPDATE_TEMPLATE':
      return (
        <Line>
          <span className="text-muted-foreground">updated template</span>
          <Chip tone="indigo">{asString(detail.name) ?? resolveTemplate(detail.template_id)}</Chip>
        </Line>
      );

    case 'DELETE_TEMPLATE':
      return (
        <Line>
          <ShieldOff className={cn('h-3 w-3 shrink-0', TONE.red.icon)} strokeWidth={1.5} />
          <span className="text-muted-foreground">deleted template</span>
          <Chip tone="red">{asString(detail.name) ?? resolveTemplate(detail.template_id)}</Chip>
        </Line>
      );

    case 'ASSIGN_TEMPLATE': {
      const overrideCount = countKeys(detail.overrides);
      return (
        <Line>
          <ShieldCheck className={cn('h-3 w-3 shrink-0', TONE.green.icon)} strokeWidth={1.5} />
          <Target />
          <span className="text-muted-foreground">assigned to</span>
          <Chip tone="green">
            {asString(detail.template_name) ?? resolveTemplate(detail.template_id)}
          </Chip>
          {overrideCount > 0 && (
            <span className="text-muted-foreground text-xs">
              +{overrideCount} override{overrideCount > 1 ? 's' : ''}
            </span>
          )}
        </Line>
      );
    }

    case 'BULK_ASSIGN_TEMPLATE': {
      const userCount = Array.isArray(detail.user_ids)
        ? detail.user_ids.length
        : typeof detail.count === 'number'
          ? detail.count
          : 0;
      return (
        <Line>
          <Users className={cn('h-3 w-3 shrink-0', TONE.cyan.icon)} strokeWidth={1.5} />
          <span className="text-muted-foreground">bulk assigned</span>
          {userCount > 0 && (
            <span className="text-foreground font-medium">
              {userCount} user{userCount !== 1 ? 's' : ''}
            </span>
          )}
          <span className="text-muted-foreground">to</span>
          <Chip tone="cyan">
            {asString(detail.template_name) ?? resolveTemplate(detail.template_id)}
          </Chip>
        </Line>
      );
    }

    case 'UPDATE_OVERRIDES': {
      const oldCount = countKeys(detail.old_overrides);
      const newCount = countKeys(detail.overrides ?? detail.new_overrides);
      return (
        <Line>
          <Key className={cn('h-3 w-3 shrink-0', TONE.violet.icon)} strokeWidth={1.5} />
          <Target />
          <span className="text-muted-foreground">overrides updated</span>
          <Chip>
            {oldCount} key{oldCount !== 1 ? 's' : ''}
          </Chip>
          <ArrowRight className="text-muted-foreground h-3 w-3 shrink-0" strokeWidth={1.5} />
          <Chip tone="violet">
            {newCount} key{newCount !== 1 ? 's' : ''}
          </Chip>
        </Line>
      );
    }

    case 'REMOVE_USER_PERMISSION': {
      const oldTemplate =
        asString(detail.template_name) ??
        (detail.template_id != null ? resolveTemplate(detail.template_id) : null);
      return (
        <Line>
          <UserMinus className={cn('h-3 w-3 shrink-0', TONE.red.icon)} strokeWidth={1.5} />
          <Target />
          <span className="text-muted-foreground">reset to default role</span>
          {oldTemplate && <Chip tone="red">{oldTemplate}</Chip>}
        </Line>
      );
    }

    case 'GRANT_ELEVATION': {
      const permCount = countKeys(detail.permissions);
      const expires = asString(detail.expires_at);
      const reason = asString(detail.reason);
      return (
        <Line>
          <ShieldCheck className={cn('h-3 w-3 shrink-0', TONE.amber.icon)} strokeWidth={1.5} />
          <Target />
          <span className="text-muted-foreground">granted</span>
          <Chip tone="amber">
            {permCount} permission{permCount !== 1 ? 's' : ''}
          </Chip>
          {expires && (
            <>
              <Clock className="text-muted-foreground h-3 w-3 shrink-0" strokeWidth={1.5} />
              <span className="text-muted-foreground text-xs">
                expires {formatDateTime(expires)}
              </span>
            </>
          )}
          {reason && (
            <span
              className="text-muted-foreground max-w-[200px] truncate text-xs italic"
              title={reason}
            >
              — {reason}
            </span>
          )}
        </Line>
      );
    }

    case 'REVOKE_ELEVATION':
      return (
        <Line>
          <ShieldOff className={cn('h-3 w-3 shrink-0', TONE.red.icon)} strokeWidth={1.5} />
          <Target />
          <span className="text-muted-foreground">elevation revoked</span>
          <Chip>ACTIVE</Chip>
          <ArrowRight className="text-muted-foreground h-3 w-3 shrink-0" strokeWidth={1.5} />
          <Chip tone="red">REVOKED</Chip>
        </Line>
      );

    default: {
      // Unknown action — key:value chips, so a new code is never a blank cell.
      const entries = Object.entries(detail);
      return (
        <Line>
          <Target />
          {entries.map(([k, v]) => (
            <span
              key={k}
              className="text-muted-foreground bg-muted/40 border-border rounded-md border px-1.5 py-0.5 text-xs"
            >
              {k}: {shortValue(v)}
            </span>
          ))}
          {!hasTarget && entries.length === 0 && <span className="text-muted-foreground">—</span>}
        </Line>
      );
    }
  }
});
